// Обновление портала после деплоя без перезагрузки на глазах у пользователя.
//
// Раньше sw.js собирался со skipWaiting + clientsClaim: новый service worker сразу забирал
// все открытые вкладки, а PwaUpdatePrompt на controllerchange делал reload. В течение
// 10 минут после деплоя страница перезагружалась у всех, кто в ней работал.
//
// Теперь новый SW ждёт (waiting), а применяем его только в безопасный момент:
//   1. портал только что открыт (или вкладка вернулась после долгого перерыва) и пользователь
//      ещё ничего не нажал: перезагрузка незаметна, новая вкладка сразу свежая;
//   2. переход на другую страницу портала: вместо SPA-перехода полная загрузка нужного адреса;
//   3. вкладка пробыла в фоне дольше HIDDEN_APPLY_AFTER_MS.
// Посреди работы на странице (карта, форма, плеер маршрута) не перезагружаем никогда.
//
// virtual:pwa-register не используем намеренно: в режиме prompt он вешает reload на
// 'controlling' в каждой вкладке, где увидел обновление, и перезагружает их все разом,
// как только любая одна вкладка применит новую версию.

import { whenRefreshSettled } from './authService'

// Проверка sw.js на сервере. Чаще 5 минут не ставить: тысячи вкладок * сутки = заметная нагрузка
const UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000
// Окно «пользователь ещё ничего не делал» после открытия или возвращения
const FRESH_WINDOW_MS = 15 * 1000
// Столько вкладка должна пробыть в фоне, чтобы обновить её тихо
const HIDDEN_APPLY_AFTER_MS = 30 * 60 * 1000
// Если новый SW не взял вкладку за это время, всё равно грузим страницу
const ACTIVATE_TIMEOUT_MS = 3000
// Столько ждём обмен refresh-токена перед перезагрузкой, дольше страницу не держим
const REFRESH_WAIT_MS = 5000

let registration = null
// Установленный, но ещё не активный новый SW. По спеке registration.waiting уже заполнен
// к событию 'installed', но на порядок событий в конкретном браузере не полагаемся
let waitingWorker = null
// Новая версия есть: SW ждёт активации или его уже активировала другая вкладка
let updateReady = false
let applying = false
let loading = false
let targetHref = null
let hiddenAt = null
let hiddenTimer = null
// С какого момента пользователь ещё ничего не нажимал
let freshSince = Date.now()
let interactedSinceFresh = false

const isFreshMoment = () => !interactedSinceFresh && Date.now() - freshSince < FRESH_WINDOW_MS

function loadTarget() {
  if (loading) return
  loading = true
  // Обновление приходит ровно тогда, когда портал открыли или вернулись после перерыва,
  // то есть вместе с обменом refresh-токена. Перезагрузка оборвала бы этот запрос: сервер токен
  // уже сменил, cookie не дошла, следующий обмен = «кража», и клиента выбрасывало на форму входа.
  Promise.race([
    whenRefreshSettled(),
    new Promise((resolve) => setTimeout(resolve, REFRESH_WAIT_MS)),
  ]).then(() => {
    const href = new URL(targetHref || window.location.href, window.location.href).href
    if (href === window.location.href) {
      window.location.reload()
    } else {
      window.location.assign(href)
    }
  })
}

function applyUpdate(href) {
  if (applying) return
  applying = true
  targetHref = href || null
  const waiting = registration?.waiting || (waitingWorker?.state === 'installed' ? waitingWorker : null)
  if (!waiting) {
    // Новый SW уже активен (его включила другая вкладка): достаточно загрузить страницу
    loadTarget()
    return
  }
  // Новый SW возьмёт вкладку, придёт controllerchange и загрузит страницу; таймер страхует
  waiting.postMessage({ type: 'SKIP_WAITING' })
  setTimeout(loadTarget, ACTIVATE_TIMEOUT_MS)
}

function scheduleHiddenApply() {
  clearTimeout(hiddenTimer)
  const left = HIDDEN_APPLY_AFTER_MS - (Date.now() - (hiddenAt ?? Date.now()))
  hiddenTimer = setTimeout(() => {
    if (document.visibilityState === 'hidden') applyUpdate()
  }, Math.max(0, left))
}

function markUpdateReady() {
  if (applying) return
  updateReady = true
  if (isFreshMoment()) {
    applyUpdate()
    return
  }
  if (document.visibilityState === 'hidden') scheduleHiddenApply()
}

function checkForUpdate() {
  registration?.update().catch(() => { /* сеть недоступна: проверим в следующий раз */ })
}

function watchInstalling(reg) {
  const sw = reg.installing
  if (!sw) return
  sw.addEventListener('statechange', () => {
    // installed при живом контроллере = новая версия ждёт; без контроллера это первая установка
    if (sw.state === 'installed' && navigator.serviceWorker.controller) {
      waitingWorker = sw
      markUpdateReady()
    }
  })
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now()
    if (updateReady) scheduleHiddenApply()
    return
  }
  clearTimeout(hiddenTimer)
  const awayMs = hiddenAt ? Date.now() - hiddenAt : 0
  hiddenAt = null
  if (awayMs >= HIDDEN_APPLY_AFTER_MS) {
    // Телефон усыпил вкладку и таймер не сработал: после долгого перерыва перезагрузка
    // выглядит как обычный запуск приложения
    if (updateReady) {
      applyUpdate()
      return
    }
    freshSince = Date.now()
    interactedSinceFresh = false
  }
  checkForUpdate()
}

export function startAppUpdates(router) {
  // Переход на другую страницу: грузим её целиком уже новой версией.
  // Первую навигацию при загрузке и смену только query/hash (фильтры в адресе) не трогаем
  router.beforeEach((to, from) => {
    if (!updateReady || !from.matched.length || to.path === from.path) return true
    applyUpdate(router.resolve(to.fullPath).href)
    return false
  })

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return

  const markInteracted = () => { interactedSinceFresh = true }
  window.addEventListener('pointerdown', markInteracted, { capture: true, passive: true })
  window.addEventListener('keydown', markInteracted, { capture: true, passive: true })
  document.addEventListener('visibilitychange', onVisibilityChange)

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (applying) {
      loadTarget()
      return
    }
    // Новую версию активировала другая вкладка: эта работает на старом коде до безопасного момента
    markUpdateReady()
  })

  // Регистрируем после события load и в простое браузера. Установка воркера
  // сразу тянет прекэш, и на первом заходе он отбирал канал у самой страницы
  // входа. Тому, у кого воркер уже стоит, задержка не вредит: страницу и так
  // отдаёт активный, а проверка обновления подождёт пару секунд.
  const base = import.meta.env.BASE_URL
  const register = () => navigator.serviceWorker.register(`${base}sw.js`, { scope: base })
    .then((reg) => {
      registration = reg
      // Новая версия уже ждала (например, открыли новую вкладку после деплоя)
      if (reg.waiting && navigator.serviceWorker.controller) markUpdateReady()
      watchInstalling(reg)
      reg.addEventListener('updatefound', () => watchInstalling(reg))
      setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS)
    })
    .catch((err) => console.warn('[app-update] service worker registration failed:', err))
  afterLoadIdle(register)
}

// После load и в простое браузера. Простоя на телефоне с открытой картой можно
// не дождаться — поэтому у requestIdleCallback стоит предел, а без него таймер.
function afterLoadIdle(fn) {
  const idle = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => fn(), { timeout: 3000 })
    else setTimeout(fn, 1500)
  }
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })
}
